// SessionGate
// -----------
// The pre-app "gate": everything that must resolve before the main portal shell
// can render — the initial loading splash, the Login screen, the password-reset
// flow, the MFA setup/challenge screens, and the multi-role/multi-shelter
// post-login chooser. Extracted verbatim from portal.jsx's stack of early
// returns (Phase 5) so the App component is no longer ~180 lines of auth-screen
// JSX. It is purely presentational: App owns the session state and passes the
// current flags + the already-bound handlers; SessionGate just renders the right
// screen, or renders `children` (the app shell) once a ready user exists.

import { useState } from "react";
import { Login } from "../components/auth/Login.jsx";
import { MfaChallenge, MfaSetupRequired } from "../components/auth/MFA.jsx";
import { PostLoginSelection } from "../components/auth/PostLoginSelection.jsx";
import { RESOURCE_NAV_IDS } from "./access";

// The Resources menu items open in a NEW browser tab (Header renders them as
// target="_blank" links). A combined user's fresh session always builds with
// sessionReady=false (they re-pick responder/picker each session), so that new tab
// would otherwise bounce them to the mode selector. Resources are mode-agnostic, so we
// let a combined user land on one without picking a mode. True when the current path's
// first segment is a Resources screen.
function isResourceRoutePath() {
  if (typeof window === "undefined") return false;
  const seg = window.location.pathname.replace(/^\/+/, "").split("/")[0];
  return RESOURCE_NAV_IDS.includes(seg);
}

function LoadingScreen() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#1B4332",
        color: "white",
        fontSize: 18,
        fontFamily: "system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif",
      }}
    >
      Loading Zephyr Portal...
    </div>
  );
}

function SetNewPasswordScreen({ onSubmit }) {
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [caps, setCaps] = useState(false);
  const [err, setErr] = useState("");

  function detectCaps(e) {
    try {
      if (e.getModifierState) setCaps(e.getModifierState("CapsLock"));
    } catch (_e) {
      /* older browsers — ignore */
    }
  }

  function submit(e) {
    e.preventDefault();
    setErr("");
    if (pwd.length < 12) {
      setErr("Password must be at least 12 characters.");
      return;
    }
    if (pwd !== confirm) {
      setErr("The two passwords don't match — please re-type them.");
      return;
    }
    onSubmit(pwd);
  }

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    fontSize: 14,
    border: "1px solid #CFE0DE",
    borderRadius: 7,
    boxSizing: "border-box",
    fontFamily: "inherit",
  };
  const labelStyle = {
    display: "block",
    fontSize: 13,
    fontWeight: 700,
    color: "#555",
    marginBottom: 4,
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#1B4332",
        padding: 20,
        fontFamily: "system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: 12,
          padding: 32,
          maxWidth: 420,
          width: "100%",
          boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
        }}
      >
        <h2 style={{ marginTop: 0, color: "#00695C", fontSize: 22 }}>Set a new password</h2>
        <p style={{ fontSize: 14, color: "#555", lineHeight: 1.5 }}>
          You arrived here from a password reset email. Please choose a new password (at least 12
          characters), then continue to the portal.
        </p>
        <form onSubmit={submit}>
          {/* New password + show/hide toggle */}
          <label htmlFor="sg-new-pwd" style={labelStyle}>
            New password
          </label>
          <div style={{ position: "relative", marginBottom: 12 }}>
            <input
              id="sg-new-pwd"
              type={show ? "text" : "password"}
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              onKeyUp={detectCaps}
              onKeyDown={detectCaps}
              placeholder="New password"
              // biome-ignore lint/a11y/noAutofocus: deliberate UX -- focus primary input on password-reset modal open
              autoFocus
              style={{ ...inputStyle, paddingRight: 56 }}
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                color: "#00897B",
                cursor: "pointer",
                font: "inherit",
                fontSize: 13,
              }}
            >
              {show ? "hide" : "show"}
            </button>
          </div>
          {/* Confirm password */}
          <label htmlFor="sg-confirm-pwd" style={labelStyle}>
            Confirm new password
          </label>
          <input
            id="sg-confirm-pwd"
            type={show ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            onKeyUp={detectCaps}
            onKeyDown={detectCaps}
            placeholder="Re-type the new password"
            style={{ ...inputStyle, marginBottom: 10 }}
          />
          {caps && (
            <div style={{ fontSize: 12, color: "#E65100", marginBottom: 8 }}>
              ⚠️ Caps Lock is on.
            </div>
          )}
          {err && <div style={{ fontSize: 13, color: "#C62828", marginBottom: 10 }}>{err}</div>}
          <button
            type="submit"
            style={{
              width: "100%",
              padding: "10px 14px",
              background: "#00695C",
              color: "white",
              border: "none",
              borderRadius: 7,
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Set password and continue
          </button>
        </form>
      </div>
    </div>
  );
}

// Returns the appropriate gate screen, or `children` once the user is signed in,
// past MFA/recovery, and session-ready. The ordering mirrors the original
// early-return stack in portal.jsx exactly.
export function SessionGate({
  loading,
  user,
  mfaRequired,
  mfaChallenge,
  passwordRecovery,
  recoveryNeedsMfa,
  onLogin,
  onRecoveryMfaSuccess,
  onRecoveryMfaCancel,
  onSubmitNewPassword,
  onMfaSetupComplete,
  onMfaChallengeSuccess,
  onMfaChallengeCancel,
  onPostLoginComplete,
  children,
}) {
  if (loading) return <LoadingScreen />;
  if (!user) return <Login onLogin={onLogin} />;
  // Password recovery -- shown when the user clicks the reset link in their email.
  // Blocks portal access until a new password is set.
  if (passwordRecovery && recoveryNeedsMfa) {
    // Original called the recovery success handler with no args -- preserve that.
    return <MfaChallenge onSuccess={() => onRecoveryMfaSuccess()} onCancel={onRecoveryMfaCancel} />;
  }
  if (passwordRecovery) {
    return <SetNewPasswordScreen onSubmit={onSubmitNewPassword} />;
  }
  // MFA states -- shown before portal access.
  if (user && mfaRequired) {
    return <MfaSetupRequired user={user} onComplete={onMfaSetupComplete} />;
  }
  if (user && mfaChallenge) {
    return <MfaChallenge onSuccess={onMfaChallengeSuccess} onCancel={onMfaChallengeCancel} />;
  }
  // Post-login selection for dual-role users or multi-shelter caseworkers.
  if (user && user.sessionReady === false) {
    // EXCEPTION: a combined user opening a mode-agnostic Resources screen in a new tab
    // shouldn't be forced to pick responder/picker just to read it — render the screen
    // (view-only; the access model treats a mode-less "combined" as internal, and
    // calendar EDIT stays gated by canAccess("calendar_manage") + RLS admin-only).
    // Scoped to combined: multi-shelter CASEWORKERS still hit the chooser (they must
    // pick a shelter, which their downstream access genuinely depends on).
    const combinedViewingResources = user.role === "combined" && isResourceRoutePath();
    if (!combinedViewingResources) {
      return <PostLoginSelection user={user} onComplete={onPostLoginComplete} />;
    }
  }
  return children;
}
